import React, { createContext, useContext, useEffect, useState } from 'react';
import { bpClient } from '../api/l1';
import {
  clearTokens,
  getAccessToken,
  getAuthSource,
  getIdToken,
  getRefreshToken,
  getTenantName,
  setAccessToken,
  setTokens,
} from '../auth/tokenStorage';
import { decodeJwtPayload, isExpired } from '../auth/decodeJwt';

interface AuthUser {
  username: string;
  email?: string;
  authSource: 'local' | 'ciam';
  tenantName?: string;
  tenantRole?: string;
}

interface ChallengeResult {
  challenge: true;
  session: string;
  username: string;
}

interface LoginResult {
  challenge: false;
}

// Distinct from ChallengeResult/LoginResult: a tenant login can also succeed at CIAM while still
// not being granted Runtime Suite access (a valid tenant user whose role has neither
// dashboard.runtime nor dashboard.customer_environment -- see the design doc Sec 4.4). 2026-08-21:
// 'challenge' now carries session/username too -- Login.tsx used to punt every tenant challenge
// out to the Control Plane's own account-recovery page, a real dead end for an invited API-only
// user (that page only accepts a numbered recovery-email code, never a raw challenge session, and
// an API-only invite has no CP dashboard account to receive that email at). Handled in-app now via
// respondToTenantChallenge, the same way the local-admin tab already handles its own challenge.
type TenantLoginResult =
  | { outcome: 'challenge'; session: string; username: string }
  | { outcome: 'not_granted'; tenantRole?: string }
  | { outcome: 'success'; tenantRole?: string };

// respondToTenantChallenge can never re-return 'challenge' -- CIAM's respond-to-challenge either
// completes the auth or throws, it doesn't chain a second challenge. Narrowed so Login.tsx's
// post-auth branch can read .tenantRole without a redundant runtime check.
type TenantChallengeCompletionResult = Exclude<TenantLoginResult, { outcome: 'challenge' }>;

interface AuthContextValue {
  isAuthenticated: boolean;
  initializing: boolean;
  user: AuthUser | null;
  login: (username: string, password: string) => Promise<ChallengeResult | LoginResult>;
  loginTenant: (tenantName: string, username: string, password: string) => Promise<TenantLoginResult>;
  respondToChallenge: (session: string, username: string, newPassword: string) => Promise<void>;
  respondToTenantChallenge: (tenantName: string, session: string, username: string, newPassword: string) => Promise<TenantChallengeCompletionResult>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The ID token is a display-only claims snapshot (username/email) -- it isn't reissued by
// /refresh, so its own exp isn't a signal that the session is over. Session validity is
// carried by the access/refresh token pair (see api/l1.ts's silent-refresh interceptor).
//
// `username` prefers, in order: Phase-1's own bare `username` claim, then
// Cognito's namespaced `cognito:username` (present on a CIAM/tenant login's ID
// token, absent on Phase-1's own), then `email`, then finally `sub` (a raw
// UUID -- last resort so this never returns null for a decodable token).
function userFromIdToken(): AuthUser | null {
  const idToken = getIdToken();
  if (!idToken) return null;
  const claims = decodeJwtPayload(idToken);
  if (!claims) return null;
  const authSource = getAuthSource() === 'ciam' ? 'ciam' : 'local';
  // `cognito:username` is this pool's raw Cognito sub (a UUID), not a login name -- prefer
  // the bare `username` claim (Phase 1's own tokens) or `email` (Cognito's) before falling
  // back to it, so a page reload doesn't regress a friendly display back to a UUID.
  const groups = (claims['cognito:groups'] as string[] | undefined) || [];
  return {
    username: claims.username || claims.email || (claims['cognito:username'] as string | undefined) || claims.sub,
    email: claims.email,
    authSource,
    tenantName: authSource === 'ciam' ? getTenantName() || undefined : undefined,
    tenantRole: authSource === 'ciam' ? groups[0] : undefined,
  };
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    (async () => {
      const accessToken = getAccessToken();
      const accessClaims = accessToken ? decodeJwtPayload(accessToken) : null;
      if (accessToken && !isExpired(accessClaims)) {
        setUser(userFromIdToken());
      } else if (getRefreshToken()) {
        // Access token missing/expired but a refresh token exists (e.g. reopening the app
        // after the 1hr access-token TTL but within the 30d refresh window) -- refresh
        // silently instead of bouncing to the login page.
        try {
          const { data } = await bpClient.post('/blueprint-runtime/iam/auth/refresh', {
            refresh_token: getRefreshToken(),
          });
          setAccessToken(data.AccessToken);
          setUser(userFromIdToken());
        } catch {
          clearTokens();
        }
      }
      setInitializing(false);
    })();
  }, []);

  async function login(username: string, password: string): Promise<ChallengeResult | LoginResult> {
    const { data } = await bpClient.post('/blueprint-runtime/iam/auth/login', { username, password });
    if (data.challenge) {
      const { Session, ChallengeParameters } = data.challenge;
      return { challenge: true, session: Session, username: ChallengeParameters?.USER_ID_FOR_SRP ?? username };
    }
    setTokens(data.tokens, { authSource: 'local' });
    setUser(userFromIdToken());
    return { challenge: false };
  }

  // Shared by loginTenant's success path and respondToTenantChallenge -- the backend returns the
  // identical shape for both (iam_auth_tenant.py's _finish_tenant_login is the one function both
  // routes return through).
  function _applyTenantLoginResponse(data: any, fallbackUsername: string): TenantChallengeCompletionResult {
    if (!data.granted) {
      return { outcome: 'not_granted', tenantRole: data.tenant_role };
    }
    setTokens(data.tokens, { authSource: 'ciam', tenantName: data.tenant_name });
    setUser({
      username: data.user?.username || data.user?.email || fallbackUsername,
      email: data.user?.email,
      authSource: 'ciam',
      tenantName: data.tenant_name,
      tenantRole: data.tenant_role,
    });
    return { outcome: 'success', tenantRole: data.tenant_role };
  }

  async function loginTenant(tenantName: string, username: string, password: string): Promise<TenantLoginResult> {
    const { data } = await bpClient.post('/blueprint-runtime/iam/auth/login-tenant', {
      tenant_name: tenantName,
      username,
      password,
    });
    if (data.challenge) {
      return { outcome: 'challenge', session: data.session, username: data.username || username };
    }
    return _applyTenantLoginResponse(data, username);
  }

  async function respondToChallenge(session: string, username: string, newPassword: string): Promise<void> {
    const { data } = await bpClient.post('/blueprint-runtime/iam/auth/respond-to-challenge', {
      session,
      username,
      new_password: newPassword,
    });
    setTokens(data, { authSource: 'local' });
    setUser(userFromIdToken());
  }

  // 2026-08-21: the tenant-pool counterpart to respondToChallenge -- see TenantLoginResult's own
  // docstring for why this exists (a real dead end this session's API-user invite flow surfaced,
  // not a hypothetical). Returns the same TenantLoginResult shape loginTenant does so Login.tsx's
  // existing not_granted/success handling covers both without a second code path.
  async function respondToTenantChallenge(tenantName: string, session: string, username: string, newPassword: string): Promise<TenantChallengeCompletionResult> {
    const { data } = await bpClient.post('/blueprint-runtime/iam/auth/respond-to-tenant-challenge', {
      tenant_name: tenantName,
      session,
      username,
      new_password: newPassword,
    });
    return _applyTenantLoginResponse(data, username);
  }

  async function logout(): Promise<void> {
    const refreshToken = getRefreshToken();
    try {
      await bpClient.post('/blueprint-runtime/iam/auth/logout', { refresh_token: refreshToken || undefined });
    } catch {
      // Best-effort server-side revocation; always clear local state regardless.
    }
    clearTokens();
    setUser(null);
    // LicenseStatusBanner.tsx's session-scoped dismissal must never carry across a login
    // boundary -- a real license problem dismissed by one session must still be visible to
    // whoever logs in next, even within the same browser tab (sessionStorage alone doesn't
    // clear on its own there).
    sessionStorage.removeItem('rtf_license_banner_dismissed');
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated: !!user, initializing, user, login, loginTenant, respondToChallenge, respondToTenantChallenge, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
